import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAtlas } from "@/context/AtlasContext";
import { cn } from "@/lib/utils";
import { Home } from "lucide-react";
import { useModes, modeFor, isItemActive, orderMiddleKeys } from "@/components/shell/nav";
import { WorkspaceSwitch, BrandLockup } from "@/components/shell/ContextPanel";
import { MoreMenu } from "@/components/shell/Rail";
import { FavoritesList } from "@/components/shell/PrefsLists";
import { usePrefs } from "@/hooks/usePrefs";
import { ScopeControl } from "@/components/shell/AppShell";

export function MobileNav() {
  const modes = useModes();
  const { rail_order } = usePrefs();
  const { ctx } = useAtlas();
  const location = useLocation();
  const [modeKey, setModeKey] = useState(() => modeFor(location.pathname).key);
  const home = modes[0];
  const bottom = modes.filter((m) => m.key === "admin");
  const middleModes = modes.filter((m) => m !== home && !bottom.includes(m));
  const orderedKeys = orderMiddleKeys(middleModes.map((m) => m.key), rail_order);
  const tabs = [home, ...orderedKeys.map((k) => middleModes.find((m) => m.key === k)).filter(Boolean), ...bottom];
  const mode = tabs.find((m) => m && m.key === modeKey) || tabs[0];
  return (
    <div className="h-full flex flex-col" style={{ background: "var(--ink-2)" }} data-testid="mobile-nav">
      <BrandLockup />
      <WorkspaceSwitch />
      {ctx?.support_mode && <div className="mx-4 mt-3 px-3 py-2 rounded-lg text-[11px] text-white flex items-center gap-2" style={{ background: "var(--signal)" }} data-testid="mobile-support-badge"><span className="font-semibold uppercase tracking-widest text-[9px]">Support mode</span> Customer workspace · audited</div>}
      {(ctx?.entities || []).length > 0 && <div className="px-4 pt-3"><ScopeControl dark /></div>}
      {mode?.key === "atlas" && <FavoritesList compact />}
      <div className="px-4 pt-4"><p className="eyebrow-light mb-2">Mode</p>
        <div className="flex flex-wrap gap-1.5" data-testid="mobile-mode-tabs">
          {tabs.map((m) => <button key={m.key} onClick={() => setModeKey(m.key)} className={cn("flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[12px] border transition-colors", modeKey === m.key ? "bg-white text-slate-900 border-white" : "text-slate-300 border-white/10 hover:bg-white/5")} data-testid={`mobile-mode-${m.key}`}><m.icon className="w-3.5 h-3.5" />{m.short || m.label}</button>)}
        </div></div>
      <nav className="flex-1 overflow-y-auto dark-scroll px-2 pt-4 pb-3">
        <NavLink to="/home" className={({ isActive }) => cn("nav-item", isActive && "active")} data-testid="mobile-nav-home"><Home className="w-4 h-4 opacity-70" /> Home</NavLink>
        {mode?.sections.map((s, i) => <div key={s.key || i} className={cn(i > 0 && "mt-3 pt-3 border-t border-white/[0.06]")}>
          {s.label && <p className="px-3 py-1 text-[12px] font-medium text-slate-400">{s.label}</p>}
          {s.items.filter((n) => n.to !== "/home").map((n) => <NavLink key={n.to} to={n.to} end={n.end} className={() => cn("nav-item", isItemActive(n, location.pathname) && "active")} data-testid={`mnav-${n.to.replace(/\//g, "-").slice(1)}`}><n.icon className="w-4 h-4 opacity-70" /> {n.label}</NavLink>)}
        </div>)}
      </nav>
      <div className="px-2 pb-3 border-t border-white/[0.06] pt-2"><MoreMenu mobile /></div>
    </div>);
}
